
import React from 'react';
import { Book as BookIcon, Music as MusicIcon } from 'lucide-react';
import AboutImageGallery from './AboutImageGallery';

const AboutBiography = () => {
  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-serif font-bold mb-8 text-center">Min musikalske rejse</h2>
          <div className="prose prose-lg max-w-none">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              <div className="col-span-2">
                <p className="mb-4 text-gray-600">
                  Mit navn er Oscar Gilbert, og min passion for klaveret begyndte i en ung alder. Allerede som barn var jeg fascineret af instrumentets mangfoldige muligheder og den rige palet af følelser, der kunne udtrykkes gennem dets tangenter.
                </p>
                <p className="mb-4 text-gray-600">
                  Efter at have studeret ved Rytmisk Musikkonservatorium, hvor jeg specialiserede mig i jazzklaver og musikpædagogik, har jeg dedikeret min karriere til at perfektionere min kunst både som udøvende pianist og som underviser.
                </p>
                <p className="mb-4 text-gray-600">
                  Som <a href="https://oscargilbert.dk/" target="_blank" rel="noopener noreferrer" className="text-piano-gold hover:underline">pianist</a> har jeg haft fornøjelsen af at optræde på forskellige scener i både ind- og udland, fra intime koncertsale til større festivaler. Jeg er primært jazzpianist og sekundært indenfor rytmisk musik: Pop, gospel, salsa, latin, rock, Højskolesangbogen og klassisk musik - jeg kan dog undervise i det hele.
                </p>
              </div>
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
                <div className="relative bg-white p-3 rounded-lg shadow-xl h-full">
                  <div className="h-full bg-gray-100 rounded-md overflow-hidden">
                    <img 
                      src="/lovable-uploads/3498deda-3d45-422b-bce5-7a5717ed23da.png" 
                      alt="Oscar Gilbert ved klaveret" 
                      className="w-full h-full object-cover"
                      width="400"
                      height="400"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <p className="mb-8 text-gray-600">
              Min tilgang til undervisning bygger på et solidt teknisk fundament kombineret med en dyb respekt for den enkelte elevs musikalske smag og mål. Jeg tror på, at god teknik er afgørende, men at glæden ved musikken og det personlige udtryk er lige så vigtigt.
            </p>

            <EducationAndSpecialties />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              <div className="col-span-2">
                <p className="mb-4 text-gray-600">
                  Gennem årene har jeg haft fornøjelsen af at undervise elever i alle aldre og på alle niveauer - fra begyndere der aldrig har rørt et klaver før, til øvede musikere der ønsker at perfektionere deres spil.
                </p>
                <p className="mb-4 text-gray-600">
                  Min undervisningsfilosofi er centreret omkring den individuelle elev. Jeg tror på, at hver person har sin egen unikke musikalske rejse, og min rolle som lærer er at guide, inspirere og udfordre på den mest hensigtsmæssige måde for den enkelte.
                </p>
                <p className="mb-4 text-gray-600">
                  Uanset om dit mål er at spille dine yndlingssange, forberede dig til en optagelsesprøve, eller simpelthen nyde glæden ved at skabe musik, ser jeg frem til at hjælpe dig på din vej.
                </p>
              </div>
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
                <div className="relative bg-white p-3 rounded-lg shadow-xl h-full">
                  <div className="h-full bg-gray-100 rounded-md overflow-hidden">
                    <img 
                      src="/lovable-uploads/9e4f3c03-ed10-4d6e-a2b0-37bdf99e4c7f.png" 
                      alt="Oscar Gilbert ved klaveret" 
                      className="w-full h-full object-cover"
                      width="400" 
                      height="400"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
            </div>

            <AboutImageGallery />
          </div>
        </div>
      </div>
    </section>
  );
};

const EducationAndSpecialties = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-xl font-serif font-semibold mb-3 flex items-center">
          <BookIcon className="mr-2 h-5 w-5 text-piano-gold" />
          Uddannelse
        </h3>
        <ul className="space-y-2 text-gray-600">
          <li>• Pianist og musiklærer, Rytmisk Musikkonservatorium</li>
          <li>• Musikalsk Grundkursus, Sankt Annæ Gymnasium</li>
          <li>• Sejergårdsskolens Musikefterskole</li>
        </ul>
      </div>
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-xl font-serif font-semibold mb-3 flex items-center">
          <MusicIcon className="mr-2 h-5 w-5 text-piano-gold" />
          Specialer
        </h3>
        <ul className="space-y-2 text-gray-600">
          <li>• Jazzklaver og improvisation</li>
          <li>• Rytmisk musik: Pop, gospel, salsa, latin, rock</li>
          <li>• Klassisk klaver</li>
          <li>• Højskolesangbogen</li>
          <li>• Musikteori og komposition</li>
          <li>• Begynderundervisning for børn og voksne</li>
        </ul>
      </div>
    </div>
  );
};

export default React.memo(AboutBiography);
